import React, { createContext, ReactNode, useContext } from 'react';

import { getOutsideOfContextHandler } from '../../utils/getOutsideOfContextHandler';
import { InteractableMap, useInteractableMap } from './use-interactable-map';
import { Canvas, useMindmapCanvas } from './use-mindmap-canvas';
import { ContextMenu, useMindmapContextMenu } from './use-mindmap-context-menu';
import { CopyCutPaste, useMindmapCopyCutPaste } from './use-mindmap-copy-cut-paste';
import { Data, useMindmapData } from './use-mindmap-data';
import { DragAndDrop, useMindmapDragAndDrop } from './use-mindmap-drag-and-drop';
import { History, useMindmapHistory } from './use-mindmap-history';
import { KeyboardNavigation, useMindmapKeyboardNavigation } from './use-mindmap-keyboard-navigation';
import { Manipulation, useMindmapManipulation } from './use-mindmap-manipulation';
import { Positioning, useMindmapPositioning } from './use-mindmap-positioning';
import { Selection, useMindmapSelection } from './use-mindmap-selection';
import { useMindmapKeyboard } from './use-mindmap-keyboard';
import { TextFormatting, useMindmapTextFormatting } from './use-mindmap-text-formatting';
import { useMindmapViewport } from './use-mindmap-viewport';

export const X_GAP = 20;
export const Y_GAP = 50;

export type Mindmap = {
  data: Data;
  canvas: Canvas;
  positioning: Positioning;
  dragAndDrop: DragAndDrop;
  selection: Selection;
  interactableMap: InteractableMap;
  manipulation: Manipulation;
  keyboardNavigation: KeyboardNavigation;
  copyCutPaste: CopyCutPaste;
  history: History;
  contextMenu: ContextMenu;
  textFormatting: TextFormatting;
};

const MindmapContext = createContext<Mindmap>({
  data: {
    nodeMap: {},
    rootNodeId: undefined,
    historizeNextMapChange: false,
    setNodeMap: getOutsideOfContextHandler('setNodeMap'),
    sethistorizeNextMapChange: getOutsideOfContextHandler('setNodeMap'),
  },
  canvas: {
    lines: {},
    addLine: getOutsideOfContextHandler('addLine'),
    removeLine: getOutsideOfContextHandler('removeLine'),
  },
  positioning: {
    boundingBox: undefined,
    positionedTree: undefined,
  },
  dragAndDrop: {
    isAnyNodeDragging: false,
    isRootNodeDragging: false,
    dropPreviewNode: undefined,
    dropDraggingElements: getOutsideOfContextHandler('dropDraggingElements'),
    onPointerMove: getOutsideOfContextHandler('onPointerMove'),
    setIsAnyNodeDragging: getOutsideOfContextHandler('setIsAnyNodeDragging'),
    setIsRootNodeDragging: getOutsideOfContextHandler('setIsRootNodeDragging'),
  },
  selection: {
    selectedNodeIds: [],
    isEditingNode: false,
    isDrawingSelection: false,
    isNodeSelected: getOutsideOfContextHandler('isNodeSelected'),
    onMapClick: getOutsideOfContextHandler('onMapClick'),
    setSelectedNodeIds: getOutsideOfContextHandler('setSelectedNodeIds'),
    toggleNodeSelection: getOutsideOfContextHandler('toggleNodeSelection'),
    unselectNodes: getOutsideOfContextHandler('unselectNodes'),
    onDrawingSelection: getOutsideOfContextHandler('onDrawingSelection'),
    setIsEditingNode: getOutsideOfContextHandler('setIsEditingNode'),
    setIsDrawingSelection: getOutsideOfContextHandler('setIsDrawingSelection'),
    updateSelectedNodeIds: getOutsideOfContextHandler('updateSelectedNodeIds'),
    selectAllNodes: getOutsideOfContextHandler('selectAllNodes'),
  },
  interactableMap: {
    interactableMapSpring: undefined,
    mindmapHeight: undefined,
    mindmapWidth: undefined,
    contextMenuOpen: false,
    contextMenuRef: React.createRef(),
    scale: getOutsideOfContextHandler('scale'),
    resetScaling: getOutsideOfContextHandler('resetScaling'),
    setMousePosition: getOutsideOfContextHandler('setMousePosition'),
    mapCoordsToPageClientCoords: getOutsideOfContextHandler('mapCoordsToPageClientCoords'),
    pageClientCoordsToMapCoords: getOutsideOfContextHandler('pageClientCoordsToMapCoords'),
    updateStyle: getOutsideOfContextHandler('updateStyle'),
    toggleContextMenu: getOutsideOfContextHandler('toggleContextMenu'),
    hideContextMenu: getOutsideOfContextHandler('hideContextMenu'),
    moveContextMenuMapCoords: getOutsideOfContextHandler('moveContextMenuMapCoords'),
    positionContextMenu: getOutsideOfContextHandler('showToolPanel'),
  },
  manipulation: {
    addChild: getOutsideOfContextHandler('addChild'),
    addSibling: getOutsideOfContextHandler('addSibling'),
    removeNode: getOutsideOfContextHandler('removeNode'),
    actions: {
      addBottomSiblingSelectable: false,
      addChildSelectable: false,
      addTopSiblingSelectable: false,
      deleteSelectable: false,
      addAttachementSelectable: false,
      onAddBottomSiblingClick: getOutsideOfContextHandler('onAddBottomSiblingClick'),
      onAddChildClick: getOutsideOfContextHandler('onAddChildClick'),
      onAddTopSiblingClick: getOutsideOfContextHandler('onAddTopSiblingClick'),
      onDeleteClick: getOutsideOfContextHandler('onDeleteClick'),
      onAddFileAttachementClick: getOutsideOfContextHandler('onAddFileAttachementClick'),
      onAddLinkAttachementClick: getOutsideOfContextHandler('onAddLinkAttachementClick'),
    },
    deleteAttachement: getOutsideOfContextHandler('deleteAttachement'),
    updateAttachement: getOutsideOfContextHandler('updateAttachement'),
    updateText: getOutsideOfContextHandler('updateText'),
    setShadowNodeAttachementsWidth: getOutsideOfContextHandler('setShadowNodeAttachementsWidth'),
    shadowNodeAttachementsWidth: undefined,
    calculateMinAttachementDimensions: getOutsideOfContextHandler('calculateMinAttachementDimensions'),
  },
  keyboardNavigation: {
    selectHigherNode: getOutsideOfContextHandler('selectHigherNode'),
    selectSiblingNode: getOutsideOfContextHandler('selectSiblingNode'),
    selectLowerNode: getOutsideOfContextHandler('selectLowerNode'),
  },
  copyCutPaste: {
    copiedTrees: [],
    hasCutNodes: false,
    copySelectedNodes: getOutsideOfContextHandler('copySelectedNodes'),
    cutSelectedNodes: getOutsideOfContextHandler('cutSelectedNodes'),
    getTreesOfNodeIds: getOutsideOfContextHandler('getTreesOfNodeIds'),
    pasteNodes: getOutsideOfContextHandler('pasteNodes'),
    revertCutting: getOutsideOfContextHandler('revertCutting'),
    setHasCutNodes: getOutsideOfContextHandler('setHasCutNodes'),
    actions: {
      onCopyClick: getOutsideOfContextHandler('onCopyClick'),
      onCutClick: getOutsideOfContextHandler('onCutClick'),
      onPasteClick: getOutsideOfContextHandler('onPasteClick'),
      onUncutClick: getOutsideOfContextHandler('onUncutClick'),
      copySelectable: false,
      cutSelectable: false,
      pasteSelectable: false,
      uncutSelectable: false,
    },
  },
  history: {
    currentUndoRedoStep: 0,
    nodeMapChangeHistory: [],
    actions: {
      redo: getOutsideOfContextHandler('redo'),
      undo: getOutsideOfContextHandler('undo'),
      redoSelectable: false,
      undoSelectable: false,
    },
  },
  contextMenu: {
    activeMenu: undefined,
    setActiveMenu: getOutsideOfContextHandler('setActiveMenu'),
    toggleContextMenu: getOutsideOfContextHandler('toggleContextMenu'),
    hideContextMenu: getOutsideOfContextHandler('hideContextMenu'),
    activeTab: -1,
    setActiveTab: getOutsideOfContextHandler('setActiveTab'),
  },
  textFormatting: {
    toggleSelectedTextFormat: getOutsideOfContextHandler('toggleSelectedTextFormat'),
    selectedTextFormats: undefined,
  },
});

export const useMindmap = () => {
  return useContext(MindmapContext);
};

interface MindmapProviderProps {
  id: string;
  children: ReactNode;
}

export const MindmapProvider = ({ id, children }: MindmapProviderProps) => {
  const data = useMindmapData({ id });
  const canvas = useMindmapCanvas();
  const positioning = useMindmapPositioning({ data });
  const interactableMap = useInteractableMap({ positioning });
  const contextMenu = useMindmapContextMenu({ interactableMap });
  const history = useMindmapHistory({ data });
  const selection = useMindmapSelection({ data, positioning, interactableMap, contextMenu });
  const viewport = useMindmapViewport({ positioning, interactableMap });
  const manipulation = useMindmapManipulation({ data, background: canvas, viewport, selection, interactableMap });
  const keyboardNavigation = useMindmapKeyboardNavigation({ data, positioning, selection, viewport });
  const copyCutPaste = useMindmapCopyCutPaste({ data, selection, manipulation, viewport });
  const dragAndDrop = useMindmapDragAndDrop({ positioning, data, selection, copyCutPaste });
  const textFormatting = useMindmapTextFormatting({ contextMenu });
  useMindmapKeyboard({
    selection,
    manipulation,
    copyCutPaste,
    history,
    keyboardNavigation,
    interactableMap,
    contextMenu,
  });

  return (
    <MindmapContext.Provider
      value={{
        canvas,
        positioning,
        dragAndDrop,
        selection,
        interactableMap,
        manipulation,
        data,
        keyboardNavigation,
        copyCutPaste,
        history,
        contextMenu,
        textFormatting,
      }}
    >
      {children}
    </MindmapContext.Provider>
  );
};
